import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { appOpened } from '@planone/bau365/data-access-fair-data';
import { NotificationService } from '@planone/bau365/util-notification';
import { SnackBarType } from '@planone/shared/util-interfaces';
import { BehaviorSubject, catchError, map, of, switchMap } from 'rxjs';

import { filter, tap, withLatestFrom } from 'rxjs/operators';

import {
  loadChannels,
  loadChannelsFailure,
  loadChannelsSuccess,
  markAsVisited,
  markAsVisitedFailure,
  markAsVisitedSuccess,
  subscribeChannel,
  subscribeChannelFailure,
  subscribeChannelRequested,
  subscribeChannelSuccess,
  unsubscribeChannel,
  unsubscribeChannelFailure,
  unsubscribeChannelSuccess,
} from './channels.actions';

import { USER_SESSION_STATUS } from '../../config/user-service.config.interface';
import { ChannelsService } from '../../service/channels/channels.service';
import { userIsLoggedIn } from '../user/data-access-user.actions';

@Injectable()
export class NewsfeedChannelsEffects {
  userIsLoggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userIsLoggedIn),
      map(() => loadChannels())
    )
  );

  loadChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChannels),
      switchMap(() =>
        this.service.getNewsfeedChannels().pipe(
          map((channels) => loadChannelsSuccess({ channels })),
          catchError((error) => of(loadChannelsFailure({ error })))
        )
      )
    )
  );

  subscribeAfterAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appOpened),
      withLatestFrom(this.activatedRoute.queryParams),
      filter(([_action, queryParams]) => queryParams['subscribe'] === '1'),
      map(([_action, queryParams]) =>
        subscribeChannel({
          fairId: queryParams['fairId'],
          channelType: queryParams['entityType'],
          id: queryParams['entityId'],
          context: queryParams['pageContext'],
        })
      )
    )
  );

  subscribeChannelRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscribeChannelRequested),
      withLatestFrom(this.userSessionStatus),
      filter(([_, activeSession]) => activeSession),
      map(([{ fairId, channelType, id, context }]) => subscribeChannel({ fairId, channelType, id, context }))
    )
  );

  subscribeChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscribeChannel),
      switchMap(({ fairId, channelType, id, context }) =>
        this.service.subscribeChannel(fairId, channelType, id).pipe(
          map((channel) => subscribeChannelSuccess({ channel, context })),
          catchError((error) => of(subscribeChannelFailure({ error })))
        )
      )
    )
  );

  unsubscribeChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(unsubscribeChannel),
      switchMap(({ fairId, channelType, id, context }) =>
        this.service.unsubscribeChannel(fairId, channelType, id).pipe(
          map((channel) => unsubscribeChannelSuccess({ channel, context })),
          catchError((error) => of(unsubscribeChannelFailure({ error })))
        )
      )
    )
  );

  subscribeChannelSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscribeChannelSuccess),
        tap(() =>
          this.notificationService.projectContent(
            $localize`:@@channelEffect-subscribeChannelSuccess-add: Zu Abonnements hinzugefügt`,
            SnackBarType.success,
            undefined,
            true
          )
        )
      ),
    { dispatch: false }
  );

  unsubscribeChannelSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unsubscribeChannelSuccess),
        tap(() =>
          this.notificationService.projectContent(
            $localize`:@@channelEffect-subscribeChannelSuccess-remove: Aus Abonnements entfernt`,
            SnackBarType.neutral,
            undefined,
            true
          )
        )
      ),
    { dispatch: false }
  );

  markAsVisited$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAsVisited),
      switchMap(({ fairId, channelType, id }) =>
        this.service.markChannelAsVisited(fairId, channelType, id).pipe(
          map((channel) => markAsVisitedSuccess({ channel })),
          catchError((error) => of(markAsVisitedFailure(error)))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private service: ChannelsService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    @Inject(USER_SESSION_STATUS) private userSessionStatus: BehaviorSubject<boolean>
  ) {}
}
